import React, { useEffect } from 'react';
import {ProfileIdentityDocumentUpload} from './profile-identity-document-upload';
import { useLazyQuery, useMutation } from '@apollo/client';
import { UploadFile } from 'antd';
import {
  ApplicantProfileIdentityDocumentUploadContainerApplicantUserUpdateDocument,
  ApplicantProfileIdentityDocumentUploadContainerApplicantUserFileCreateAuthHeaderDocument,
  ApplicantProfileIdentityDocumentUploadContainerApplicantUserDocument,
  FileInfo,
  ApplicantUserNameOnDocumentAssetInput
} from '../../../../generated';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { AuthResult, AzureBlobResponse } from '../../../shared/file-upload/azure-upload';
import { getItem } from '../../../shared/local-storage';
import { sanitizeFilename } from '../../../../utils/filename';

interface ProfileIdentityDocumentUploadContainerProps {}

export const ProfileIdentityDocumentUploadContainer: React.FC<ProfileIdentityDocumentUploadContainerProps> = () => {
  const applicantUserId = getItem('applicantUserId');
  const [updateAssets] = useMutation(ApplicantProfileIdentityDocumentUploadContainerApplicantUserUpdateDocument);
  const [createNewAssetHeader] = useMutation(ApplicantProfileIdentityDocumentUploadContainerApplicantUserFileCreateAuthHeaderDocument);

  const [isUploadProcessing, setIsUploadProcessing] = React.useState(false);

  const [getFilesData, { data: filesData }] = useLazyQuery(ApplicantProfileIdentityDocumentUploadContainerApplicantUserDocument);

  const fetchFilesData = () => {
    getFilesData({
      variables: {
        applicantUserId: applicantUserId
      },
      fetchPolicy: 'network-only'
    });
    setIsUploadProcessing(false);
  };

  useEffect(() => {
    fetchFilesData();
  }, []);

  const addNewAsset = async (file: File): Promise<AuthResult> => {
    setIsUploadProcessing(true);
    return createNewAssetHeader({
      variables: {
        input: {
          applicantId: applicantUserId,
          fileName: sanitizeFilename(file.name),
          contentType: file.type,
          contentLength: file.size
        }
      }
    })
      .then((response) => {
        return {
          ...response?.data?.applicantUserFileCreateAuthHeader,
          ...{ isAuthorized: true }
        } as AuthResult;
      })
      .catch((error) => {
        console.error('Error uploading asset: ', error);
        return { isAuthorized: false } as AuthResult;
      });
  };

  const onAddAsset = async (response: AzureBlobResponse, _file: UploadFile) => {
    let alreadyExists = false;
    const filename = response.blobUrl.split('/').pop() ?? '';
    const formattedData: ApplicantUserNameOnDocumentAssetInput[] =
      filesData?.applicantUser?.personalInformation?.nameOnDocumentAssets?.map((doc: any) => {
        if (filename === (doc.documentName ?? '')) {
          alreadyExists = true;
          return {
            id: doc.id,
            documentName: doc.documentName,
            isDeleteRequested: doc.isDeleteRequested
          };
        }
        return {
          id: doc.id,
          isDeleteRequested: doc.isDeleteRequested
        };
      }) ?? [];

    if (!alreadyExists) {
      formattedData?.push({
        documentName: filename
      });
    }

    const input = {
      id: applicantUserId,
      applicantUserDetails: {
        personalInformation: {
          nameOnDocumentAssets: [...(formattedData || [])]
        }
      }
    };
    await updateAssets({
      variables: {
        input
      }
    });
    fetchFilesData();
  };

  return (
    <ComponentQueryLoader
      loading={false}
      hasData={true}
      hasDataComponent={
        <ProfileIdentityDocumentUpload
          isProcessing={isUploadProcessing}
          nameOnDocumentAssets={filesData?.applicantUser?.personalInformation?.nameOnDocumentAssets as any[]}
          assetUpdateFunction={addNewAsset}
          onAssetUpdate={onAddAsset}
          files={filesData?.applicantUser?.files as FileInfo[]}
        />
      }
    />
  );
};

